import i18next from "i18next";

const RECEIPT_TEMPLATE_PATH = "/receipt/receipt-template.js";

/**
 * Gets the base URL for public assets.
 * Uses PUBLIC_ASSETS_URL env var (e.g. https://cdn.wompi.co)
 */
function getAssetsBaseUrl() {
  return String(process.env.PUBLIC_ASSETS_URL || "https://cdn.wompi.co");
}

/**
 * Loads the receipt template from the public assets CDN.
 * Caches the generateReceipt function after first load.
 */
let cachedGenerateReceipt = null;

async function loadReceiptTemplate() {
  if (cachedGenerateReceipt) return cachedGenerateReceipt;

  const url = getAssetsBaseUrl() + RECEIPT_TEMPLATE_PATH;

  // Load the template by injecting it as a script tag (avoids new Function / eval)
  return new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = url;
    script.onload = () => {
      if (typeof globalThis.generateReceipt === "function") {
        cachedGenerateReceipt = globalThis.generateReceipt;
        resolve(cachedGenerateReceipt);
      } else {
        reject(new Error("generateReceipt not found after loading script"));
      }
    };
    script.onerror = () =>
      reject(new Error("Failed to load receipt template script"));
    document.head.appendChild(script);
  });
}

/**
 * Formats cents to a currency string.
 * COP uses no decimals (e.g. 2000000 → "$20.000")
 * USD/GTQ use 2 decimals (e.g. 6186 → "$61.86")
 */
export function formatAmount(amountInCents, currency = "COP") {
  const currencyConfig = {
    COP: { locale: "es-CO", fractionDigits: 0 },
    USD: { locale: "en-US", fractionDigits: 2 },
    GTQ: { locale: "es-GT", fractionDigits: 2 },
  };

  const config = currencyConfig[currency] || {
    locale: "es-CO",
    fractionDigits: 2,
  };
  const amount = amountInCents / 100;
  const formatted = amount.toLocaleString(config.locale, {
    minimumFractionDigits: config.fractionDigits,
    maximumFractionDigits: config.fractionDigits,
  });
  return `$${formatted}`;
}

/**
 * Formats a date string to "DD/MM/YYYY – HH:mm"
 */
export function formatDate(dateString) {
  if (!dateString) return "";
  const d = new Date(dateString);
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = d.getFullYear();
  const hours = String(d.getHours()).padStart(2, "0");
  const minutes = String(d.getMinutes()).padStart(2, "0");
  return `${day}/${month}/${year} \u2013 ${hours}:${minutes}`;
}

/**
 * Gets the payment method display string
 */
export function getPaymentMethodLabel(paymentMethod) {
  if (!paymentMethod) return "";
  const extra = paymentMethod.extra || {};
  if (extra.lastFour) {
    return `************${extra.lastFour}`;
  }
  return paymentMethod.type || "";
}

/**
 * Gets an SVG icon for the payment brand
 */
export function getPaymentMethodIcon(paymentMethod) {
  if (!paymentMethod?.extra) return "";
  const brand = (
    paymentMethod.extra.brand ||
    paymentMethod.type ||
    ""
  ).toUpperCase();
  return `<svg width="40" height="14" viewBox="0 0 40 14" fill="none" xmlns="http://www.w3.org/2000/svg"><rect width="40" height="14" rx="2" fill="#1A1F71"/><text x="20" y="10.5" text-anchor="middle" fill="white" font-size="7" font-weight="bold" font-family="Arial">${brand}</text></svg>`;
}

/**
 * Gets i18n labels for the receipt template
 */
function getLabels() {
  return {
    payTo: i18next.t("receipt.payTo", "Pago a"),
    transactionTitle: i18next.t(
      "receipt.transactionTitle",
      "Información de la transacción",
    ),
    transactionId: i18next.t("receipt.transactionId", "Transacción #"),
    reference: i18next.t("receipt.reference", "Referencia"),
    paymentMethod: i18next.t("receipt.paymentMethod", "Método de pago"),
    buyerTitle: i18next.t("receipt.buyerTitle", "Información del comprador"),
    name: i18next.t("receipt.name", "Nombre"),
    phone: i18next.t("receipt.phone", "Teléfono"),
    email: i18next.t("receipt.email", "Email"),
    securePayments: i18next.t("receipt.securePayments", "PAGOS SEGUROS POR"),
  };
}

/**
 * Maps transaction status to receipt status
 */
export function mapStatus(status) {
  const map = {
    APPROVED: "APPROVED",
    DECLINED: "DECLINED",
    ERROR: "ERROR",
    PENDING: "PENDING",
    VOIDED: "VOIDED",
  };
  return map[status] || "ERROR";
}

/**
 * Generates and opens a receipt in a new tab.
 * Loads the template from the public assets CDN, generates the HTML,
 * and opens it in a new browser tab with auto-print.
 *
 * @param {Object} transaction - The transaction object from the store
 * @param {string} merchantName - The merchant name
 */
export async function downloadReceiptPdf(transaction, merchantName) {
  const customerData = transaction.customerData || {};
  const pm = transaction.paymentMethod || {};
  const extra = pm.extra || {};

  // Resolve buyer info with fallbacks
  const buyerName = customerData.fullName || extra.fullName || pm.name || "";
  const buyerPhone = customerData.phoneNumber || extra.cellphoneNumber || pm.phoneNumber || "";
  const buyerEmail = transaction.customerEmail || extra.email || "";

  let generateReceipt;
  try {
    generateReceipt = await loadReceiptTemplate();
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error("[Receipt] Failed to load template:", err);
    return;
  }

  const receiptHtml = generateReceipt({
    status: mapStatus(transaction.status),
    merchantName: merchantName || "",
    amount: formatAmount(transaction.amountInCents, transaction.currency),
    currency: transaction.currency || "COP",
    date: formatDate(transaction.createdAt),
    transactionId: String(transaction.id || ""),
    reference: transaction.reference || "",
    paymentMethod: getPaymentMethodLabel(transaction.paymentMethod),
    paymentMethodIcon: getPaymentMethodIcon(transaction.paymentMethod),
    buyerName: buyerName,
    buyerPhone: buyerPhone,
    buyerEmail: buyerEmail,
    labels: getLabels(),
  });

  // Inject a script that auto-triggers print after load
  const htmlWithAutoPrint = receiptHtml.replace(
    "</body>",
    "<script>window.onload=function(){setTimeout(function(){window.print();},400);};<" +
      "/script></body>",
  );

  // Open the receipt in a new tab using a Blob URL (cross-browser compatible)
  const blob = new Blob([htmlWithAutoPrint], { type: "text/html" });
  const url = URL.createObjectURL(blob);
  globalThis.open(url, "_blank");
}
